import { useCallback, useEffect, useState } from 'react';
import { Building2, MonitorSmartphone, Users, RefreshCw } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { AnalyticsDay, TenantActivity, TerminalUptime, MedicLoad } from '../models';
import { getCheckupsByDay, getTenantsActivity, getTerminalUptime, getMedicLoad, getApiError } from '../services/api';
import { cn } from '../lib/utils';

export const AnalyticsPage = () => {
  const [checkupsByDay, setCheckupsByDay] = useState<AnalyticsDay[]>();
  const [tenantsActivity, setTenantsActivity] = useState<TenantActivity[]>();
  const [terminalUptime, setTerminalUptime] = useState<TerminalUptime[]>();
  const [medicLoad, setMedicLoad] = useState<MedicLoad[]>();
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [day, tenants, uptime, medics] = await Promise.all([
        getCheckupsByDay(30).catch(() => null),
        getTenantsActivity().catch(() => null),
        getTerminalUptime().catch(() => null),
        getMedicLoad().catch(() => null),
      ]);
      if (day) setCheckupsByDay(day.data.data);
      if (tenants) setTenantsActivity(tenants.data.data);
      if (uptime) setTerminalUptime(uptime.data.data);
      if (medics) setMedicLoad(medics.data.data);
    } catch (e) {
      setError(getApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const maxCheckups = Math.max(1, ...(checkupsByDay || []).map(d => d.count));

  return (
    <AppLayout title="Аналитика">
      <PageHeader
        title="Аналитика и отчёты"
        description="Статистика осмотров, загрузка медиков, uptime терминалов и активность клиентов."
        action={<Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /> Обновить</Button>}
      />
      {error && <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

      {!checkupsByDay && !error ? <LoadingState /> : (
        <div className="space-y-6">
          {/* Checkups by Day Chart */}
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold">Осмотры по дням</h2>
                <p className="text-xs text-muted-foreground">Последние 30 дней</p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" /> Всего</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success" /> Вердикт</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-destructive" /> Ошибки</span>
              </div>
            </div>
            <div className="flex items-end gap-1" style={{ height: 200 }}>
              {(checkupsByDay || []).map((day) => (
                <div key={day.day} className="group relative flex-1">
                  <div className="flex flex-col items-center gap-0.5">
                    <div
                      className="w-full rounded-t bg-primary/80 transition-colors group-hover:bg-primary"
                      style={{ height: `${(day.count / maxCheckups) * 160}px`, minHeight: 2 }}
                    />
                    {day.error_count > 0 && (
                      <div
                        className="absolute bottom-0 w-full rounded-t bg-destructive/80"
                        style={{ height: `${(day.error_count / maxCheckups) * 160}px` }}
                      />
                    )}
                  </div>
                  <div className="mt-1 text-center text-[9px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                    {day.day.slice(5)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Tenant Activity */}
            <div className="rounded-2xl border border-border bg-card shadow-sm">
              <div className="border-b border-border p-4">
                <h2 className="flex items-center gap-2 text-sm font-bold"><Building2 className="h-4 w-4" /> Активность клиентов</h2>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {!tenantsActivity ? <LoadingState /> : tenantsActivity.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">Нет данных</div>
                ) : (
                  <div className="divide-y divide-border">
                    {tenantsActivity.map((t, i) => (
                      <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                        <span className="w-6 text-center text-xs font-bold text-muted-foreground">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{t.terminal_count} терминалов</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{t.total_checkups}</p>
                          <p className="text-xs text-muted-foreground">осмотров</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Terminal Uptime */}
            <div className="rounded-2xl border border-border bg-card shadow-sm">
              <div className="border-b border-border p-4">
                <h2 className="flex items-center gap-2 text-sm font-bold"><MonitorSmartphone className="h-4 w-4" /> Uptime терминалов</h2>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {!terminalUptime ? <LoadingState /> : terminalUptime.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">Нет данных</div>
                ) : (
                  <div className="divide-y divide-border">
                    {terminalUptime.map((t) => (
                      <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                        <div className={cn(
                          'h-2.5 w-2.5 rounded-full',
                          t.uptime_score >= 75 ? 'bg-success' : t.uptime_score >= 50 ? 'bg-warning' : 'bg-destructive'
                        )} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{t.tenantName}</p>
                        </div>
                        <div className="text-right">
                          <p className={cn('text-sm font-bold', t.uptime_score >= 75 ? 'text-success' : t.uptime_score >= 50 ? 'text-warning' : 'text-destructive')}>
                            {t.uptime_score}%
                          </p>
                          <p className="text-xs text-muted-foreground">{t.isOnline ? 'online' : 'offline'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Medic Load */}
          <div className="rounded-2xl border border-border bg-card shadow-sm">
            <div className="border-b border-border p-4">
              <h2 className="flex items-center gap-2 text-sm font-bold"><Users className="h-4 w-4" /> Загрузка медиков</h2>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {!medicLoad ? <LoadingState /> : medicLoad.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Нет данных</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-3">Медик</th>
                        <th className="px-4 py-3">Организация</th>
                        <th className="px-4 py-3 text-center">Вердиктов</th>
                        <th className="px-4 py-3 text-center">Активных</th>
                        <th className="px-4 py-3">Очередь</th>
                      </tr>
                    </thead>
                    <tbody>
                      {medicLoad.map((m) => (
                        <tr key={m.id} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                          <td className="px-4 py-3 font-medium">{m.name}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{m.tenantName}</td>
                          <td className="px-4 py-3 text-center font-mono font-bold">{m.verdict_count}</td>
                          <td className="px-4 py-3 text-center font-mono">{m.active_count}</td>
                          <td className="px-4 py-3">
                            <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
                              m.isQueueAvailable ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                            )}>
                              {m.isQueueAvailable ? 'Доступен' : 'Недоступен'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};
